import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Building2, Check, ChevronDown, Search } from 'lucide-react';
import api from '../api/client';

const OrgSwitcher = () => {
  const { user, organization, switchOrganization } = useAuth();
  const { setOrganization: setOrgContext } = useOrg();
  const [organizations, setOrganizations] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  // Only show for SuperAdmins
  if (user?.role !== 'SuperAdmin') {
    return null;
  }

  const fetchOrganizations = useCallback(async () => {
    if (loaded) return;
    setLoading(true);
    try {
      const response = await api.get('/superadmin/organizations/summary');
      const activeOrgs = response.data
        .filter(org => org.status !== 'inactive')
        .sort((a, b) => {
          if (a.isSystemOrg) return -1;
          if (b.isSystemOrg) return 1;
          return (a.name || '').localeCompare(b.name || '');
        });
      setOrganizations(activeOrgs);
      setLoaded(true);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    } finally {
      setLoading(false);
    }
  }, [loaded]);

  const handleOrgSwitch = async (org) => {
    if (org.id === organization?.id) return;
    try {
      if (switchOrganization) switchOrganization(org);
      if (setOrgContext) setOrgContext(org);
      const orgSlug = org.subdomain || org.slug;
      navigate(orgSlug ? `/${orgSlug}/courses` : '/courses');
      window.dispatchEvent(new CustomEvent('org-switched', { detail: org }));
    } catch (error) {
      console.error('Error switching organization:', error);
    }
  };

  const filteredOrgs = searchQuery
    ? organizations.filter(org =>
        org.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        org.subdomain?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        org.type?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : organizations;

  return (
    <DropdownMenu onOpenChange={(open) => { if (open) { fetchOrganizations(); setSearchQuery(''); } }}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex items-center gap-2 px-3 py-2 h-auto hover:bg-gray-100"
          data-testid="org-switcher"
        >
          <div className="flex items-center gap-2">
            {organization?.compactLogoUrl ? (
              <img 
                src={organization.compactLogoUrl} 
                alt={organization.name}
                className="w-6 h-6 object-contain"
                width={24}
                height={24}
              />
            ) : (
              <Building2 className="w-4 h-4 text-gray-600" />
            )}
            <div className="flex flex-col items-start">
              <span className="text-sm font-medium text-gray-900">
                {organization?.name || 'Select Organization'}
              </span>
              <span className="text-xs text-gray-500">Switch Organization</span>
            </div>
          </div>
          <ChevronDown className="w-4 h-4 text-gray-500" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="text-xs text-gray-500 uppercase">
          Organizations
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {/* Search Field */}
        <div className="px-2 py-1.5" onKeyDown={(e) => e.stopPropagation()}>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search organizations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400"
              data-testid="org-switcher-search"
              autoFocus
            />
          </div>
        </div>
        <DropdownMenuSeparator />

        <div className="max-h-64 overflow-y-auto">
          {loading ? (
            <div className="px-2 py-4 text-center text-sm text-gray-500">Loading...</div>
          ) : filteredOrgs.length === 0 && loaded ? (
            <div className="px-2 py-4 text-center text-sm text-gray-500">
              {searchQuery ? 'No matching organizations' : 'No organizations found'}
            </div>
          ) : (
            filteredOrgs.map((org) => {
              const isSelected = org.id === organization?.id;
              return (
                <DropdownMenuItem
                  key={org.id}
                  onClick={() => handleOrgSwitch(org)}
                  className={`flex items-center gap-2 cursor-pointer ${org.isSystemOrg ? 'bg-blue-50' : ''}`}
                >
                  <div className="flex items-center gap-2 flex-1">
                    {org.compactLogoUrl ? (
                      <img 
                        src={org.compactLogoUrl} 
                        alt={org.name}
                        className="w-5 h-5 object-contain"
                        width={20}
                        height={20}
                      />
                    ) : (
                      <Building2 className={`w-4 h-4 ${org.isSystemOrg ? 'text-blue-600' : 'text-gray-400'}`} />
                    )}
                    <div className="flex-1">
                      <div className="font-medium text-sm">{org.name}</div>
                      {org.isSystemOrg ? (
                        <div className="text-xs text-blue-600">Platform Administration</div>
                      ) : org.type && (
                        <div className="text-xs text-gray-500">{org.type}</div>
                      )}
                    </div>
                  </div>
                  {isSelected && <Check className="w-4 h-4 text-green-600" />}
                  {org.isSystemOrg ? (
                    <Badge variant="outline" className="text-xs border-blue-500 text-blue-600">System</Badge>
                  ) : org.status === 'active' ? (
                    <Badge variant="outline" className="text-xs">Active</Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">{org.status}</Badge>
                  )}
                </DropdownMenuItem>
              );
            })
          )}
        </div>

        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => navigate('/superadmin')}
          className="text-sm text-blue-600 hover:text-blue-700 cursor-pointer"
        >
          <Building2 className="w-4 h-4 mr-2" />
          Manage Organizations
        </DropdownMenuItem>
        
        {organization?.subdomain && (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-2 text-xs text-gray-500">
              <div className="font-medium mb-1">Direct Access URL:</div>
              <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">
                /{organization.subdomain || organization.slug}/login
              </code>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default OrgSwitcher;
